//@flow
import I18nHelper from 'common/helpers/I18nHelper';
import EntityImportDataHelper from 'common/entity/helpers/EntityImportDataHelper';
import DataUtils from 'common/utils/DataUtils';
import {
    BusinessUnitId,
    type BusinessUnitIdModelType,
    type BusinessUnitModelType,
    type BusinessUnitListModelType
} from 'common/models/flow/BusinessUnits';

import type { MasterTeamModelType } from 'common/models/flow/MasterTeam';
import { EntityImportHelper } from 'common/entity/helpers/EntityImportHelper';
import * as Immutable from 'immutable';
import { MasterTeam } from 'common/models/internal/MasterTeam';
import { CityId } from 'common/models/internal/CityId';
import type { CityIdModelType } from 'common/models/flow/CityId';

type nsTranslateType = string => string;
const translationsNamespace = ['entityManagement'];
const nsTranslate: nsTranslateType = I18nHelper.getTranslate(
    translationsNamespace
);

export class TeamImportHelper extends EntityImportDataHelper {
    static processBusinessUnitId({
        cellValue,
        clientId
    }: {
        cellValue: string,
        clientId: string
    }) {
        return { clientId, businessUnitId: cellValue };
    }

    static serializeBusinessUnitId(businessUnitId: BusinessUnitIdModelType) {
        return BusinessUnitId.fromJS(businessUnitId);
    }

    static validateBusinessUnitId({
        entity,
        businessUnits
    }: {
        entity: MasterTeamModelType,
        businessUnits: BusinessUnitListModelType & Array<BusinessUnitModelType>
    }): ?string {
        const businessUnitId = entity.businessUnitId?.businessUnitId;
        if (DataUtils.isEmpty(businessUnitId)) {
            return;
        }
        const isValidBusinessUnit = businessUnits.some(
            businessUnit => businessUnit.businessUnitId === businessUnitId
        );
        if (!isValidBusinessUnit) {
            return nsTranslate('teamMasterValidations.invalidBusinessUnitId');
        }
    }

    static processCityId({
        cellValue
    }: {
        cellValue: string
    }): CityIdModelType {
        return { cityId: cellValue };
    }

    static serializeCityId(cityId: CityIdModelType): ?CityId {
        return CityId.fromJS(cityId);
    }

    static validateCityId({
        entity,
        cityIds
    }: {
        entity: MasterTeamModelType,
        cityIds: Array<string>
    }): ?string {
        const cityId = entity.cityId?.cityId;
        if (DataUtils.isEmpty(cityId)) {
            return;
        }
        const isValidCityId = cityIds.includes(cityId);
        if (!isValidCityId) {
            return nsTranslate('teamMasterValidations.invalidCityId');
        }
    }

    static processEntityRows = ({ rows }) => {
        return rows.map(entityRow =>
            entityRow.map((cellValue, headerId) => {
                if (headerId === 'customProperties') {
                    return cellValue.filter(value => value);
                }
                return cellValue;
            })
        );
    };

    static getUploadedEntities = ({
        uploadedTable,
        clientId,
        getDataForCell
    }): Immutable.List<MasterTeam> => {
        const teamsData = EntityImportHelper.makeDataForEntities({
            table: uploadedTable,
            clientId,
            getDataForCell,
            processEntityRows: TeamImportHelper.processEntityRows
        });
        return Immutable.List(
            teamsData.map(teamEntity => MasterTeam.fromJS(teamEntity.toJS()))
        );
    };

    static generateEntityList = (
        teamsData: Array<*>
    ): Immutable.List<MasterTeam> =>
        Immutable.List(
            teamsData.map(teamEntity => MasterTeam.fromJS(teamEntity))
        );

    static getCustomFields(entitySettings) {
        return entitySettings?.teamSettings?.customFields;
    }
}
export default TeamImportHelper;
